import type { SingleCondition, ConditionSource, ComparisonOperator, ConditionValue } from "@capture/shared/types";
import type { FieldDefinitionSummary } from "../hooks/useJobTypeFields";
import { FormulaField } from "../form-config/shared/FormulaField";
import { AGGREGATE_RESULT_TYPE, DOC_SYSTEM_VARIABLES, BATCH_SYSTEM_VARIABLES, USER_SYSTEM_VARIABLES } from "@capture/shared";

/** Minimal LookupTable summary the condition editor needs for the picker. */
export interface ConditionLookupTable {
  id: string;
  name: string;
  columns: string[];
}

interface ConditionRowProps {
  condition: SingleCondition;
  fields: FieldDefinitionSummary[];
  onChange: (condition: SingleCondition) => void;
  onRemove: () => void;
  /** Tenant lookup tables — enables the `lookup` condition source (F-024). */
  lookupTables?: ConditionLookupTable[];
  /** P8 — job-level constant NAMES. When non-empty, the value-type picker
   *  offers "A named constant…" and lists these. */
  constants?: string[];
}

const BASE_SOURCE_TYPES = [
  { value: "field", label: "Field value" },
  { value: "field_group", label: "Field group" },
  { value: "expression", label: "Formula" },
  { value: "document", label: "Document property" },
  { value: "batch", label: "Batch property" },
  { value: "user", label: "Acting user" },
  { value: "confidence", label: "Field confidence" },
];
const LOOKUP_SOURCE_TYPE = { value: "lookup", label: "Lookup table" };

const OPERATORS: Record<string, Array<{ value: ComparisonOperator; label: string }>> = {
  text: [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "not equals" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "starts_with", label: "starts with" },
    { value: "ends_with", label: "ends with" },
    { value: "matches_regex", label: "matches pattern" },
    { value: "in", label: "is one of" },
    { value: "not_in", label: "is not one of" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "is not empty" },
  ],
  number: [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "not equals" },
    { value: "greater_than", label: "greater than" },
    { value: "less_than", label: "less than" },
    { value: "greater_or_equal", label: "at least" },
    { value: "less_or_equal", label: "at most" },
    { value: "between", label: "between" },
    { value: "in", label: "is one of" },
    { value: "not_in", label: "is not one of" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "is not empty" },
  ],
  date: [
    { value: "equals", label: "is" },
    { value: "not_equals", label: "is not" },
    { value: "greater_than", label: "is after" },
    { value: "less_than", label: "is before" },
    { value: "greater_or_equal", label: "on or after" },
    { value: "less_or_equal", label: "on or before" },
    { value: "between", label: "between" },
    { value: "in", label: "is one of" },
    { value: "not_in", label: "is not one of" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "is not empty" },
  ],
  boolean: [
    { value: "equals", label: "is" },
    { value: "not_equals", label: "is not" },
    { value: "is_empty", label: "is empty" },
    { value: "is_not_empty", label: "is not empty" },
  ],
};

const AGGREGATES: Array<{ value: string; label: string }> = [
  { value: 'sum', label: 'sum of' },
  { value: 'count_filled', label: 'count filled' },
  { value: 'count_checked', label: 'count checked' },
  { value: 'any', label: 'any checked' },
  { value: 'all', label: 'all checked' },
  { value: 'none', label: 'none checked' },
];
const isBooleanAggregate = (agg: string) =>
  AGGREGATE_RESULT_TYPE[agg as keyof typeof AGGREGATE_RESULT_TYPE] === 'boolean';

const NUMERIC_DATA_TYPES = new Set(["number", "currency", "decimal"]);

function getOperatorsForSource(source: ConditionSource, fields: FieldDefinitionSummary[]) {
  if (source.type === "confidence") return OPERATORS.boolean!;
  if (source.type === "field") {
    const field = fields.find((f) => f.name === source.name);
    const dt = field?.dataType ?? "text";
    if (NUMERIC_DATA_TYPES.has(dt)) return OPERATORS.number!;
    if (dt === "date") return OPERATORS.date!;
    if (dt === "boolean") return OPERATORS.boolean!;
  }
  if (source.type === "field_group") {
    return isBooleanAggregate(source.aggregate) ? OPERATORS.boolean! : OPERATORS.number!;
  }
  if (source.type === "expression") return OPERATORS.number!;
  if (source.type === "document" || source.type === "batch") {
    const registry = source.type === "document" ? DOC_SYSTEM_VARIABLES : BATCH_SYSTEM_VARIABLES;
    const dt = registry.find((v) => v.key === source.property)?.dataType ?? "text";
    if (dt === "number") return OPERATORS.number!;
    if (dt === "date") return OPERATORS.date!;
    return OPERATORS.text!; // text + text-list (contains / in / not_in live here)
  }
  if (source.type === "user") {
    // roles is a text-list (contains / is one of); id/teamId are text — all
    // live under the text operator set.
    return OPERATORS.text!;
  }
  return OPERATORS.text!;
}


/** F-042 — date-ness for the relative-date value options. Reuses the exact
 *  check `getOperatorsForSource` already applies to a `field` source's
 *  `dataType` so "is a date field" can never disagree between the operator
 *  list and the value picker. */
function isDateFieldSource(source: ConditionSource, fields: FieldDefinitionSummary[]): boolean {
  if (source.type !== "field") return false;
  const field = fields.find((f) => f.name === source.name);
  return (field?.dataType ?? "text") === "date";
}

export function ConditionRow({ condition, fields, onChange, onRemove, lookupTables = [], constants = [] }: ConditionRowProps) {
  const operators = getOperatorsForSource(condition.source, fields);
  const isConfidence = condition.source.type === "confidence";
  const isDateField = isDateFieldSource(condition.source, fields);
  const SOURCE_TYPES = lookupTables.length > 0 ? [...BASE_SOURCE_TYPES, LOOKUP_SOURCE_TYPE] : BASE_SOURCE_TYPES;
  // The selected lookup table's columns (for match/return column pickers).
  const lookupCols =
    condition.source.type === "lookup"
      ? lookupTables.find((t) => t.id === (condition.source as Extract<ConditionSource, { type: "lookup" }>).table)?.columns ?? []
      : [];

  function updateSource(partial: Partial<ConditionSource>) {
    const newSource = { ...condition.source, ...partial } as ConditionSource;
    onChange({ ...condition, source: newSource });
  }

  function updateOperator(op: ComparisonOperator) {
    onChange({ ...condition, operator: op });
  }

  function updateValue(partial: Partial<ConditionValue>) {
    const newValue = { ...condition.value, ...partial } as ConditionValue;
    onChange({ ...condition, value: newValue });
  }

  return (
    <div className="condition-row">
      {/* Source type selector */}
      <select
        className="cond-select cond-source-type"
        value={condition.source.type}
        onChange={(e) => {
          const type = e.target.value as ConditionSource["type"];
          if (type === "field") onChange({ ...condition, source: { type: "field", name: fields[0]?.name ?? "" } });
          else if (type === "expression") onChange({ ...condition, source: { type: "expression", formula: "" } });
          else if (type === "document") onChange({ ...condition, source: { type: "document", property: "classification" } });
          else if (type === "batch") onChange({ ...condition, source: { type: "batch", property: "documentCount" } });
          else if (type === "user") onChange({ ...condition, source: { type: "user", property: "id" } });
          else if (type === "field_group") onChange({ ...condition, source: { type: "field_group", names: [], aggregate: "count_filled" } });
          else if (type === "lookup") {
            const t = lookupTables[0];
            onChange({
              ...condition,
              source: {
                type: "lookup",
                table: t?.id ?? "",
                matchField: t?.columns[0] ?? "",
                matchValue: fields[0] ? `{${fields[0].name}}` : "",
                returnField: t?.columns[1] ?? t?.columns[0] ?? "",
              },
            });
          }
          else if (type === "confidence") onChange({
            ...condition,
            source: { type: "confidence", scope: "any_field", below: 0.8 },
            operator: "equals",
            value: { type: "literal", value: true },
          });
        }}
      >
        {SOURCE_TYPES.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </select>

      {/* Source-specific selector */}
      {condition.source.type === "field" && (
        <select
          className="cond-select cond-field"
          value={condition.source.name}
          onChange={(e) => updateSource({ name: e.target.value })}
        >
          {fields.map((f) => (
            <option key={f.id} value={f.name}>{f.label}</option>
          ))}
        </select>
      )}

      {condition.source.type === "field_group" && (() => {
        const groupSource = condition.source as Extract<ConditionSource, { type: "field_group" }>;
        const { aggregate, names } = groupSource;
        return (
          <div className="cond-fieldgroup">
            <select
              className="cond-select cond-aggregate"
              data-testid="cond-aggregate"
              value={aggregate}
              onChange={(e) => {
                const nextAggregate = e.target.value as "count_checked" | "count_filled" | "sum" | "any" | "all" | "none";
                const nextSource: ConditionSource = { type: "field_group", names, aggregate: nextAggregate };
                const categoryChanged = isBooleanAggregate(nextAggregate) !== isBooleanAggregate(aggregate);
                if (categoryChanged) {
                  onChange({ ...condition, source: nextSource, operator: "equals" });
                } else {
                  onChange({ ...condition, source: nextSource });
                }
              }}
            >
              {AGGREGATES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
            <div className="cond-fieldgroup-names" data-testid="cond-fieldgroup-names">
              {fields.map((f) => {
                const checked = names.includes(f.name);
                return (
                  <label key={f.id} className="cond-fieldgroup-name">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const updatedNames = e.target.checked ? [...names, f.name] : names.filter((n) => n !== f.name);
                        onChange({
                          ...condition,
                          source: { type: "field_group", names: updatedNames, aggregate },
                        });
                      }}
                    />
                    {f.label}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })()}

      {condition.source.type === "expression" && (
        <FormulaField
          value={{ source: condition.source.formula }}
          onChange={(next) => updateSource({ formula: next.source })}
          fieldNames={fields.map((f) => f.name)}
          context="rule"
          ariaLabel="Condition formula"
        />
      )}

      {condition.source.type === "document" && (
        <select className="cond-select" value={condition.source.property}
          onChange={(e) => updateSource({ property: e.target.value })}>
          {DOC_SYSTEM_VARIABLES.map((v) => (
            <option key={v.key} value={v.key} title={v.description}>{v.label}</option>
          ))}
        </select>
      )}

      {condition.source.type === "batch" && (
        <select className="cond-select" value={condition.source.property}
          onChange={(e) => updateSource({ property: e.target.value })}>
          {BATCH_SYSTEM_VARIABLES.map((v) => (
            <option key={v.key} value={v.key} title={v.description}>{v.label}</option>
          ))}
        </select>
      )}

      {condition.source.type === "user" && (
        <select className="cond-select cond-user-property" data-testid="cond-user-property"
          value={condition.source.property}
          onChange={(e) => updateSource({ property: e.target.value as "id" | "roles" | "teamId" })}
          aria-label="Acting user property">
          {USER_SYSTEM_VARIABLES.map((v) => (
            <option key={v.key} value={v.key} title={v.description}>{v.label}</option>
          ))}
        </select>
      )}

      {/* F-024 — first-class keyed lookup source (was orphaned in the engine):
          pick a table, the column to match, the value to match (a field ref),
          and the column to return — then compare to a field below. */}
      {condition.source.type === "lookup" && (() => {
        const src = condition.source as Extract<ConditionSource, { type: "lookup" }>;
        const matchFieldName = src.matchValue.replace(/^\{(\w+)\}$/, "$1");
        return (
          <div className="cond-lookup">
            <select
              className="cond-select cond-lookup-table" data-testid="cond-lookup-table"
              value={src.table}
              onChange={(e) => {
                const t = lookupTables.find((x) => x.id === e.target.value);
                updateSource({ table: e.target.value, matchField: t?.columns[0] ?? "", returnField: t?.columns[1] ?? t?.columns[0] ?? "" });
              }}
            >
              {lookupTables.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select
              className="cond-select cond-lookup-match-column" data-testid="cond-lookup-match-column"
              value={src.matchField}
              onChange={(e) => updateSource({ matchField: e.target.value })}
              aria-label="Match column"
            >
              {lookupCols.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="cond-lookup-eq">=</span>
            <select
              className="cond-select cond-lookup-match-value" data-testid="cond-lookup-match-value"
              value={matchFieldName}
              onChange={(e) => updateSource({ matchValue: `{${e.target.value}}` })}
              aria-label="Match value field"
            >
              {fields.map((f) => <option key={f.id} value={f.name}>{f.label}</option>)}
            </select>
            <span className="cond-lookup-arrow">→</span>
            <select
              className="cond-select cond-lookup-return-column" data-testid="cond-lookup-return-column"
              value={src.returnField}
              onChange={(e) => updateSource({ returnField: e.target.value })}
              aria-label="Return column"
            >
              {lookupCols.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        );
      })()}

      {condition.source.type === "confidence" && (() => {
        const src = condition.source as Extract<ConditionSource, { type: "confidence" }>;
        return (
          <div className="cond-confidence">
            <select
              className="cond-select cond-confidence-scope" data-testid="cond-confidence-scope"
              value={src.scope}
              onChange={(e) => {
                const scope = e.target.value as "any_field" | "field";
                updateSource(scope === "field" ? { scope, name: src.name ?? fields[0]?.name ?? "" } : { scope, name: undefined });
              }}
              aria-label="Confidence scope"
            >
              <option value="any_field">any field</option>
              <option value="field">a specific field</option>
            </select>
            <span className="cond-confidence-below-label">below</span>
            <input
              className="cond-input cond-confidence-below" data-testid="cond-confidence-below"
              type="number" min={0} max={1} step={0.05}
              value={src.below}
              onChange={(e) => updateSource({ below: Number(e.target.value) })}
              aria-label="Confidence threshold"
            />
            {src.scope === "field" && (
              <select
                className="cond-select cond-confidence-field" data-testid="cond-confidence-field"
                value={src.name ?? ""}
                onChange={(e) => updateSource({ name: e.target.value })}
                aria-label="Confidence field"
              >
                {fields.map((f) => <option key={f.id} value={f.name}>{f.label}</option>)}
              </select>
            )}
          </div>
        );
      })()}

      {/* Operator selector */}
      {!isConfidence && (
        <select
          className="cond-select cond-operator"
          value={condition.operator}
          onChange={(e) => updateOperator(e.target.value as ComparisonOperator)}
        >
          {operators.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {/* Value-type selector + value editor (hidden for is_empty/is_not_empty) */}
      {!isConfidence && !["is_empty", "is_not_empty"].includes(condition.operator) && (
        <>
          <select
            className="cond-select cond-value-type"
            data-testid="cond-value-type"
            value={
              // F-042 round-trip: an existing relative-date value maps back to
              // whichever option produced it — "today" for days===0, "relative"
              // for anything else — so re-opening a saved condition shows the
              // option the author actually picked.
              condition.value.type === "relative-date"
                ? (condition.value.days === 0 ? "today" : "relative")
                : condition.value.type
            }
            onChange={(e) => {
              const t = e.target.value;
              if (t === 'literal') onChange({ ...condition, value: { type: 'literal', value: '' } });
              else if (t === 'field') onChange({ ...condition, value: { type: 'field', name: fields[0]?.name ?? '' } });
              else if (t === 'list') onChange({ ...condition, value: { type: 'list', values: [] } });
              else if (t === 'constant') onChange({ ...condition, value: { type: 'constant', name: constants[0] ?? '' } });
              else if (t === 'expression') onChange({ ...condition, value: { type: 'expression', formula: '' } });
              else if (t === 'today') onChange({ ...condition, value: { type: 'relative-date', days: 0 } });
              else if (t === 'relative') onChange({ ...condition, value: { type: 'relative-date', days: -1 } });
            }}
          >
            {/* F-042 — likely-first layering (design §3): date fields lead with
                today / days-from-today, ahead of the generic literal/field/list
                options, which stay byte-identical for non-date fields. */}
            {isDateField && <option value="today">Today</option>}
            {isDateField && <option value="relative">Days from today…</option>}
            <option value="literal">{isDateField ? "A specific date…" : "value"}</option>
            <option value="field">{isDateField ? "Another field…" : "field"}</option>
            <option value="list">list</option>
            {/* P4 T3 — compare against a computed formula (resolver support
                already exists); editor is the same FormulaField the Formula
                SOURCE uses. */}
            <option value="expression">A formula…</option>
            {/* P8 — headline authoring win: compare against a job-level named
                constant. Shown only when the job declares any. */}
            {constants.length > 0 && <option value="constant">A named constant…</option>}
          </select>

          {condition.value.type === "relative-date" && (() => {
            const relValue = condition.value;
            if (relValue.days === 0) {
              return (
                <span className="cond-relative-date" data-testid="cond-relative-date">
                  today
                </span>
              );
            }
            const direction = relValue.days > 0 ? "after" : "before";
            return (
              <div className="cond-relative-date" data-testid="cond-relative-date">
                <input
                  className="cond-input cond-relative-date-days"
                  data-testid="cond-relative-date-days"
                  type="number"
                  min={0}
                  value={Math.abs(relValue.days)}
                  onChange={(e) => {
                    const n = Number(e.target.value) || 0;
                    onChange({ ...condition, value: { type: 'relative-date', days: direction === 'before' ? -n : n } });
                  }}
                />
                <select
                  className="cond-select cond-relative-date-direction"
                  data-testid="cond-relative-date-direction"
                  value={direction}
                  onChange={(e) => {
                    const nextDirection = e.target.value;
                    const n = Math.abs(relValue.days) || 1;
                    onChange({ ...condition, value: { type: 'relative-date', days: nextDirection === 'before' ? -n : n } });
                  }}
                >
                  <option value="before">before</option>
                  <option value="after">after</option>
                </select>
              </div>
            );
          })()}

          {condition.value.type === "literal" && (
            <input
              className="cond-input cond-value"
              type="text"
              value={String(condition.value.value ?? "")}
              onChange={(e) => {
                const v = e.target.value;
                const numVal = Number(v);
                updateValue({ value: v !== "" && !isNaN(numVal) ? numVal : v });
              }}
              placeholder="value"
            />
          )}
          {condition.value.type === "field" && (
            <select
              className="cond-select"
              value={condition.value.name}
              onChange={(e) => updateValue({ name: e.target.value })}
            >
              {fields.map((f) => (
                <option key={f.id} value={f.name}>{f.label}</option>
              ))}
            </select>
          )}
          {condition.value.type === "list" && (() => {
            const listValues = condition.value.values;
            // E-054 P4 follow-up — when the source is date-typed (same operator-
            // table identity check the operator list uses, so the two can never
            // disagree) and the operator is in/not_in, build the list with a real
            // date input: one removable ISO token per date. Produces exactly the
            // tokens the publish gate (`condition.value.list-invalid`) validates.
            // Non-date lists keep the comma text input byte-identical.
            if (operators === OPERATORS.date && (condition.operator === "in" || condition.operator === "not_in")) {
              return (
                <div className="cond-date-list" data-testid="cond-date-list">
                  {listValues.map((v) => (
                    <span key={v} className="cond-date-list-token">
                      {v}
                      <button
                        type="button"
                        className="cond-date-list-remove"
                        aria-label={`Remove ${v}`}
                        onClick={() =>
                          onChange({ ...condition, value: { type: 'list', values: listValues.filter((x) => x !== v) } })
                        }
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  <input
                    className="cond-input cond-date-list-input"
                    data-testid="cond-date-list-input"
                    type="date"
                    value=""
                    aria-label="Add a date"
                    onChange={(e) => {
                      const iso = e.target.value;
                      if (!iso || listValues.includes(iso)) return;
                      onChange({ ...condition, value: { type: 'list', values: [...listValues, iso] } });
                    }}
                  />
                </div>
              );
            }
            return (
              <input
                className="cond-input cond-value-list"
                data-testid="cond-value-list"
                type="text"
                value={listValues.join(', ')}
                onChange={(e) => {
                  const values = e.target.value.split(',').map((s) => s.trim()).filter((s) => s !== '');
                  onChange({ ...condition, value: { type: 'list', values } });
                }}
                placeholder="comma,separated,values"
              />
            );
          })()}
          {condition.value.type === "constant" && (
            <select
              className="cond-select cond-value-constant"
              data-testid="cond-value-constant"
              value={condition.value.name}
              onChange={(e) => updateValue({ name: e.target.value })}
              aria-label="Named constant"
            >
              {constants.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          )}
          {condition.value.type === "expression" && (
            <FormulaField
              value={{ source: condition.value.formula }}
              onChange={(next) => updateValue({ formula: next.source })}
              fieldNames={fields.map((f) => f.name)}
              context="rule"
              ariaLabel="Condition value formula"
            />
          )}
        </>
      )}

      <button className="cond-remove" onClick={onRemove} title="Remove condition">
        &times;
      </button>
    </div>
  );
}
